import { useCallback, useRef, useState, type CSSProperties, type ReactNode } from "react"
import { AgentChatPanel } from "@/components/agent/agent-chat-panel"
import { AgentColumnHeader } from "@/components/workspace/agent-column-header"
import { useAgentColumnChrome } from "@/components/workspace/agent-column-chrome-context"
import { decorateAssistantTabs } from "@/components/ui/assistant-tab-icon"
import { FloatingTabPill, type FloatingTabPillTab } from "@/components/ui/floating-tab-pill"
import type { WorkspaceBottomDockAgentProps } from "@/components/workspace/workspace-bottom-dock"
import { useTranslation } from "@/hooks/use-translation"
import { chatAgentLabelKey } from "@/lib/task-agents"
import { useAgentColumnStore } from "@/stores/agent-column-store"
import { cn } from "@/lib/utils"

type WorkspaceAgentSideColumnProps = {
  tabs: FloatingTabPillTab[]
  /**
   * Tabbed column (studio, canvas, shot, flow node, assets, 3D scenes): transcript and footer
   * are two stable regions with the tab pill on their border. The header stays on top and names
   * the active tab, `bodySlot` covers the transcript, `children` sits in the composer's cell,
   * so the pill never moves. Without it the column is assistant-only.
   */
  pinnedFooter?: boolean
  /** With `pinnedFooter`: show `bodySlot` and `children` instead of the chat and composer. */
  profileActive?: boolean
  /** Content over the transcript (top area of the non-assistant tab). */
  bodySlot?: ReactNode
  /** Footer of the non-assistant tab, stacked with the composer. */
  children?: ReactNode
  agent: WorkspaceBottomDockAgentProps
  toolbar?: ReactNode
  dirtyAriaLabel?: string
  className?: string
  style?: CSSProperties
}

/**
 * Desktop agent column: chat history on top, prompt/assistant switcher on the
 * top edge of the composer block. Collapse lives on the shell toolbar, not on the column.
 */
export function WorkspaceAgentSideColumn({
  tabs,
  profileActive = false,
  pinnedFooter = false,
  bodySlot,
  agent,
  children,
  toolbar,
  dirtyAriaLabel,
  className,
  style,
}: WorkspaceAgentSideColumnProps) {
  const { t } = useTranslation("workspace")
  const trackChrome = useAgentColumnChrome()
  const setBusy = useAgentColumnStore((state) => state.setBusy)
  const noteReply = useAgentColumnStore((state) => state.noteReply)
  const [assistantBusy, setAssistantBusy] = useState(false)
  const onBusyChangeRef = useRef(agent.onBusyChange)
  const onAssistantSettledRef = useRef(agent.onAssistantSettled)
  onBusyChangeRef.current = agent.onBusyChange
  onAssistantSettledRef.current = agent.onAssistantSettled
  const handleBusyChange = useCallback(
    (busy: boolean) => {
      setAssistantBusy(busy)
      if (trackChrome) setBusy(busy)
      onBusyChangeRef.current?.(busy)
    },
    [setBusy, trackChrome],
  )
  const handleAssistantSettled = useCallback(
    (preview: string) => {
      noteReply(preview)
      onAssistantSettledRef.current?.(preview)
    },
    [noteReply],
  )
  const tabPill =
    tabs.length >= 2 ? (
      <FloatingTabPill dirtyAriaLabel={dirtyAriaLabel} tabs={decorateAssistantTabs(tabs, assistantBusy)} />
    ) : undefined

  const agentTitle = agent.title ?? t(chatAgentLabelKey(agent.mode, agent.contextType))
  // Не на вкладке ассистента шапка называет активную вкладку (Медиа, Профиль, Промпт…).
  const profileTab =
    pinnedFooter && profileActive ? tabs.find((tab) => tab.active && tab.id !== "assistant") : undefined

  return (
    <aside
      className={cn(
        "flex h-full min-h-0 w-full shrink-0 flex-col",
        "dark:bg-zinc-950/40 light:bg-ui-bg-elevated/80",
        className,
      )}
      style={style}
    >
      <AgentColumnHeader
        toolbar={toolbar}
        title={profileTab?.label ?? agentTitle}
        iconNode={profileTab ? (profileTab.icon ?? null) : undefined}
      />

      <div className="relative min-h-0 min-w-0 flex-1">
        <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col">
          <AgentChatPanel
            {...agent}
            onBusyChange={handleBusyChange}
            onAssistantSettled={trackChrome ? handleAssistantSettled : agent.onAssistantSettled}
            expanded
            showExpandToggle={false}
            collapseOnOutsideClick={false}
            fillAvailableHeight
            chromeless
            hideTitle
            anchor="inline"
            zIndexClass="z-10"
            className="h-full min-h-0"
            bodySlot={pinnedFooter ? bodySlot : undefined}
            bodySlotVisible={pinnedFooter && profileActive}
            pinComposerSlot={pinnedFooter}
            composerSlotVisible={pinnedFooter && profileActive}
            composerSlot={pinnedFooter ? children : undefined}
            composerEdgeOverlay={tabPill}
            composerEdgeOverlayFillWidth={pinnedFooter}
          />
        </div>
      </div>
    </aside>
  )
}
